import { UsageService } from '../../../../core/usage/usage.service';
import { ChatService } from '../../../../chat/chat.service';
import { appointmentPrefill, chatAppointmentCard } from '../chat-action-request';
import { closestAppointmentType } from '../../../../chat/core/actions';
import { ThemeService } from './../../../../core/services/theme.service';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import {
  combineLatest, EMPTY, forkJoin, of, startWith, switchMap, take, takeUntil, tap,
} from 'rxjs';
import { AppointmentTypeService } from 'src/app/core/services/appointment-type.service';
import { LocationService } from 'src/app/core/services/location.service';
import { CalendarLocationFilterService } from 'src/app/core/services/calendar-location-filter.service';
import { defaultLocationId, typesForLocation } from 'src/app/core/utils/practice-locations';
/*
 * §23.1.1 — the late-document prompt. A PURE module beside this one, with the clock passed in,
 * because the whole of its awkwardness is calendar-day arithmetic and a rule that reads the clock
 * itself can only be tested by moving the clock.
 */
import {
  LateDocumentDecision, LateDocumentLink, lateDocumentDecisions, lateDocumentLines,
  sendNowDecisions,
} from './appointment-late-documents';
import { DocumentAttachmentService } from
  '../../../../shared/components/document-attachment-picker/document-attachment.service';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { ColleagueService } from './../../../../core/services/colleague.service';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { PatientService } from './../../../../core/services/patient.service';
import { PlannedCodeInput, PlannedProcedureCodeService, UnconsumedMotivation } from './../../../../core/services/planned-procedure-code.service';
import { ProcedureCode } from './../../../../core/services/app-data.service';
import { ScheduleService } from './../../../../core/services/schedule.service';
/**
 * §25 — DOCUMENTS THAT WERE NEVER SENT, ON THE APPOINTMENT THAT DID NOT SEND THEM.
 *
 * §23.1 puts the skip on the appointment for three stated reasons: `PatientDocumentRequests`
 * already carries `appointmentId`, so no new column and no new join; a flag on the appointment
 * could not say WHICH document was skipped; and a row makes the skip ACTIONABLE — the send-now
 * below is the existing manual send against an existing row, not a new construction.
 */
import {
  DocumentSkipDescription,
  DocumentSkipRow,
  PatientDocumentSkipService,
  describeDocumentSkip,
  patientHasNoChannel,
} from './../../../../core/services/patient-document-skip.service';
import { UserService } from 'src/app/core/services/user.service';
import { ANESTHESIA, DEFAULT_EMAILS } from './../../../../core/data/app.data';
import { fillMessagePlaceholders } from './../../../../core/utils/reschedule-messages';
import { addMinutesToTime, effectiveDurationMinutes } from './../../../../core/utils/appointment-duration';
import { shouldWarnOfConflict } from 'src/app/core/utils/slot-capacity';
import { patientGroupId } from 'src/app/core/utils/patient-group';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

/**
 * §3d — one chip in the booking's procedure-code selector.
 *
 * `ProcedureCode` has no `quantity`; it carries `units`, which is the TARIFF's unit value out of
 * the codebook and not a count of anything the user chose. `PlannedProcedureCode.quantity` is a
 * real column, so it rides along as an extra property rather than being folded into `units`,
 * where it would be read as tariff data by everything else that touches a chip.
 *
 * The selector keeps the objects it is handed (`updateChips` slices `initialCodes` and
 * `codesChanged` re-emits the same references), so a quantity that arrives from the server
 * SURVIVES a round trip through the UI. A chip the user adds has no quantity and the server
 * defaults it to 1, which is the same thing `createFromAiRequest` has always done.
 */
type PlannedChip = ProcedureCode & { quantity?: number };

/**
 * A chip for a stored planned code. The four fields `ProcedureCode` requires and this path has
 * no value for are filled with empties rather than cast away: the selector reads `type` and
 * `tags` when it renders a chip, and `undefined` there renders differently from `''`.
 */
function plannedChip(code: string, description: string | null | undefined, quantity?: number): PlannedChip {
  return {
    date: '',
    code,
    description: description || '',
    units: '',
    type: '',
    tags: [],
    quantity,
  };
}

@Component({
  selector: 'app-patient-appointment-detail',
  templateUrl: './patient-appointment-detail.component.html',
  styleUrls: ['./patient-appointment-detail.component.scss']
})
export class PatientAppointmentDetailComponent extends BaseComponent implements OnInit {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  private readonly chat = inject(ChatService);
  /** The conversation a chat's `/appointment` opened this page from, or null. */
  chatConversationId: string | null = null;

  todayDate: moment.Moment = moment();

  form!: FormGroup;
  editingAppointment: any;
  patientId!: string;
  groupId!: string;
  scheduleSettings?: any;
  timeSlots: { value: string, viewValue: string }[] = [];
  appointmentTypes: any[] = [];
  /** The group's practice locations; the Location picker shows only when there are some. */
  locations: any[] = [];
  /** The appointment types offered at the chosen location (all of them when no location is chosen). */
  typeOptions: any[] = [];
  /**
   * True once the user has typed an End Time by hand — `tasks/appointment-type-duration.md`.
   *
   * The end is DERIVED (start + the chosen type's effective duration) until they do, and a
   * derived end follows a change of type. A typed end does not: a receptionist who set a
   * deliberate 60 minutes and then corrected the type must not have that clobbered. A change of
   * START clears the flag again, because moving a 45-minute booking from 09:00 to 10:00 means
   * 10:45, not the 09:45 they typed earlier.
   *
   * For an existing booking the flag is inferred by VALUE in patchForm(): stored end equal to the
   * derived end means "derived", anything else means "typed". No column records it.
   */
  endSlotOverridden = false;
  groupUsers: any[] = [];
  /**
   * The practice's hospitals, for the procedure-only Hospital select. Comes straight off
   * `HospitalService.hospitals$`, which the service itself keeps loaded from the selected group —
   * no fetch of our own. Its BehaviorSubject seeds with `null`, hence the `|| []`.
   */
  hospitals: any[] = [];

  // ═══ §3b — the theatre list is a DAY EXPORT, so its inputs live on the appointment ══════
  //
  // `TheatreLists`/`TheatreListCases` are gone as a place to type these. Anaesthetist,
  // assistants, anaesthesia type, theatre number, post-op ward and special instructions are now
  // columns on `PatientAppointment` and are captured here, on the only row a procedure has.
  //
  // NOTHING BELOW COPIES A VALUE FORWARD. §3b decision 3: the owner accepted more typing on a
  // six-case day rather than have a name arrive on a case it does not belong to. There is
  // deliberately no "same as the last booking" shortcut, no seeding from the previous
  // appointment, and no defaulting from the appointment type — every value is typed or picked.

  /** `Colleague` rows with `isAnaesthetist`. Server-filtered — see `loadTheatreRoster`. */
  anaesthetists: any[] = [];

  /** `Colleague` rows with `isSurgicalAssistant`. Both assistant pickers read this one list. */
  surgicalAssistants: any[] = [];

  /**
   * §5.5 "Anaesthetist ≠ anaesthetic": this is the TYPE, and it is the VeriClaim day list's
   * "Anaesthetics" column. The full strings are used as the option values on purpose — the
   * server stores what the select emits, and a short form ('GA', 'TIVA') would not match the
   * `ANESTHESIA` list the rest of the app renders.
   */
  anaesthesiaTypes = ANESTHESIA;

  currentPatient: any;
  isProcedure: boolean = false;
  /** The appointment type's Disable Communication. */
  private typeSuppressesSending = false;
  /** Nothing is sent for this booking: the type says so, or the practice is a state unit (tasks/state-mode.md). */
  get suppressSending(): boolean {
    return this.stateMode || this.typeSuppressesSending;
  }
  set suppressSending(value: boolean) {
    this.typeSuppressesSending = value;
  }

  /** A state unit (tasks/state-mode.md): bookings never notify or remind the patient. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  /** Procedure codes planned on a booking: a state unit records ICD-10 codes only. */
  get showPlannedCodes(): boolean {
    return !this.stateMode;
  }
  /**
   * §30. The documents this booking will send, pre-ticked from the appointment type's triggers and
   * overridable per booking (§14.3: configuration pre-ticks, it never forces).
   */
  selectedDocumentIds: string[] = [];

  /**
   * §23.1.1 — HAS THE PICKER ACTUALLY SAID ANYTHING YET?
   *
   * `selectedDocumentIds` starts `[]`, and `[]` is genuinely ambiguous on this screen: it is what
   * the field holds before the picker has loaded, AND what it holds after somebody unticks every
   * document. The server never sees that ambiguity — by the time a body is built the picker has
   * spoken — but the late-document prompt asks its question earlier, and reading "not loaded yet"
   * as "the user wants none of them" would silently suppress the prompt.
   *
   * So the emission is recorded, rather than inferred from the array's emptiness.
   */
  private documentSelectionKnown = false;

  /** The picker's `(selectionChange)`. */
  onDocumentSelectionChange(ids: string[]): void {
    this.selectedDocumentIds = ids || [];
    this.documentSelectionKnown = true;
    // Attaching a document IS a decision to contact the patient — see `mustNotifyForDocuments`.
    if (this.selectedDocumentIds.length) this.notifyPatient = true;
  }

  /**
   * §25.1 — NOTIFYING IS NOT OPTIONAL ONCE A DOCUMENT IS ATTACHED.
   *
   * A document reaches a patient as a LINK in a message. `notifyPatient === false` is one of the
   * two conditions that gate the whole document block server-side, so a booking with documents
   * attached and notifications off raises every one of them as `skipped`/`suppressed` — the
   * practice ticked documents, saved, and nothing went anywhere.
   *
   * The toggle is therefore forced ON and disabled while anything is attached, rather than left
   * to be discovered afterwards on the patient file. Unticking the documents releases it.
   *
   * Only on a NEW booking: an edit raises no documents (`raiseAppointmentDocumentRequests` runs
   * from `addPatientAppointment` alone), so there is nothing there for the toggle to contradict.
   */
  get mustNotifyForDocuments(): boolean {
    return !this.editingAppointment && this.selectedDocumentIds.length > 0;
  }

  private notifyChoice = true;
  get notifyPatient(): boolean {
    return !this.stateMode && this.notifyChoice;
  }
  set notifyPatient(value: boolean) {
    this.notifyChoice = value;
  }
  /** The reminder choice saved with the booking: never one for a state unit. */
  get reminderForSave(): boolean | null {
    return this.stateMode ? false : this.reminderChoice;
  }
  /** Whether the patient gets the appointment reminder, as the switch shows it. */
  sendReminder = true;
  /** This booking's own choice, saved with it; null follows its type. */
  reminderChoice: boolean | null = null;
  private typeSendsReminders = true;

  /**
   * Blocks re-entry into saveAppointment(). The save button stays enabled while we await the
   * slot-availability check (and any conflict dialog), so a double click would otherwise fire
   * two POSTs and create two identical appointments.
   */
  isSaving: boolean = false;

  // ═══ §3d — THE CODES THIS BOOKING PLANS, ON THE BOOKING SCREEN ══════════════════════
  //
  // tasks/theatre-booking-and-procedure-lifecycle.md §3d. Every theatre booking now shows the
  // procedure codes that will be linked to it, editable, in all three workflows — including
  // the one that used to reach the hospital with an empty grid because the surgeon never wrote
  // a letter of motivation (§3 called that "a stated gap, not the workflow").
  //
  // WHAT REPLACED WHAT. C3 offered "attach the letter of motivation?" as a confirm dialog per
  // motivation, and attaching was invisible — the codes never appeared on this screen. The
  // dialog is gone for the ordinary case: the codes are PREFILLED into the selector below,
  // where the user can see them and change them. A dialog asking permission to show something
  // editable is a dialog people dismiss without reading.
  //
  // WHAT DID NOT CHANGE. §7's "offer, never infer" still holds, because it is a rule about not
  // GUESSING WHICH MOTIVATION belongs to a booking:
  //
  //   - Nothing here reads a date or sorts by recency to pick a winner.
  //   - Exactly ONE unconsumed motivation prefills. It is visible and one click from empty.
  //   - TWO OR MORE prefill nothing and are offered one at a time, as before. Choosing between
  //     them is the inference §7 forbids, and it stays the user's to make.
  //   - A booking that already carries codes is never overwritten by a motivation.

  /** The chips, and the whole state of the selector. Emitted straight to the replace-set save. */
  plannedCodes: PlannedChip[] = [];

  /**
   * The motivation these codes were prefilled from, and the codes AS THE LETTER HAD THEM.
   *
   * Two jobs. It is the `sourceAiRequestId` the save sends, which is what lets the server adopt
   * the letter's existing rows rather than create lookalikes — so the letter's provenance
   * survives and the motivation stops being offered. And it is the baseline the drift marker
   * below is measured against.
   *
   * Null for a booking made with no letter, which is workflow 3 and is a perfectly ordinary
   * state: there is then nothing to warn against, and nothing warns.
   */
  motivationBaseline: { sourceAiRequestId: string; motivatedAt: string; codes: string[] } | null = null;

  /**
   * True once the planned codes for THIS appointment have been read from the server.
   *
   * The save is gated on it. Without it, opening an existing theatre booking and pressing Save
   * before the codes arrive would send an empty replace-set and wipe the grid — the exact
   * silent half-write this workstream exists to prevent.
   */
  private plannedCodesLoaded = false;

  /** Blocks a second load while one is in flight, e.g. from flipping the type back and forth. */
  private plannedCodesInFlight = false;

  /**
   * Which booking the selector's contents were loaded for — an appointment id, or `'new'`.
   *
   * A LOAD IS NOT IDEMPOTENT FROM THE USER'S SIDE, which is the reason this exists. Three
   * things can ask for one (opening an existing appointment, the `editingAppointment$` stream
   * emitting, and picking a procedure type), they arrive in no fixed order, and a second load
   * would re-prefill from the motivation over edits the user had already made. So a booking is
   * loaded once and the answer stands until the booking changes.
   */
  private plannedCodesLoadedForKey: string | null = null;

  /**
   * AI requests already offered in this component instance, so switching the appointment type
   * back and forth does not re-ask. Session-scoped ON PURPOSE, and different from the C2
   * decline, which is persisted: this is a "not right now" about one booking, not a decision
   * about the motivation. The motivation stays in the offer list for the next booking.
   */
  private offeredMotivationAiRequestIds = new Set<string>();


  // Status-related properties
  statusOptions: { value: string, label: string, available: boolean }[] = [];
  currentStatus: string = 'pending';

  darkTheme!: NgxMaterialTimepickerTheme;

  /** Synced from `selectedGroup$` so UI updates when the group stream emits. */
  calendarSplitByPhysician = false;
  /** The doctor whose calendar column the booking was started from; the type's default does not replace them. */
  calendarPhysicianId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private hospitalService: HospitalService,
    private patientService: PatientService,
    private user: UserService,
    private appointmentService: PatientAppointmentService,
    // §23.1.1 — the appointment type's document links, for the late-document prompt.
    private documentAttachmentService: DocumentAttachmentService,
    private scheduleService: ScheduleService,
    private appointmentTypeService: AppointmentTypeService,
    private locationService: LocationService,
    private calendarLocationFilter: CalendarLocationFilterService,
    private plannedProcedureCodeService: PlannedProcedureCodeService,
    private colleagueService: ColleagueService,
    private themeService: ThemeService,
    private patientDocumentSkipService: PatientDocumentSkipService,
    private cd: ChangeDetectorRef,


    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();
    this.route.paramMap.pipe(
      takeUntil(this.destroy$),
      switchMap(params => {
        const appointmentId = params.get('id');
        if (appointmentId) {
          // Use case 2: Edit an existing appointment
          return this.appointmentService.getAppointmentById(appointmentId);
        } else {
          // Use case 1 or 3: New appointment
          return of(null);
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe(appointment => {
      if (appointment && appointment.data) {
        // Use case 2: Patch the form with the retrieved appointment data
        this.patchForm(appointment.data);
        this.editingAppointment = appointment.data;
        // Editing an existing booking: opt IN to notifying the patient. Most edits are status
        // or note changes, and defaulting to ON sent "Rescheduled" emails nobody intended.
        this.notifyPatient = false;
        // §3d — AFTER `editingAppointment` is set, never inside patchForm(). The loader keys on
        // the appointment id to decide "read this booking's codes" vs "this is a new booking",
        // and patchForm runs one line too early to have one.
        this.maybeLoadPlannedCodes();
        // §25 — and the documents this booking did NOT send, for the same reason and in the same
        // place: the id exists here and nowhere earlier.
        this.loadSkippedDocuments();
      } else {
        // Use case 1 or 3: Reset the form for a new appointment
        this.resetForm();
        // after the reset, which waits a tick
        setTimeout(() => this.applyChatPrefill());
        this.usage.flow('appointment', 'start');

        // Use case 3: Check for patientId and date in query params
        this.route.queryParams
          .pipe(
            takeUntil(this.destroy$),
            take(1)
          ).subscribe(params => {
            const patientId = params['patientId'];
            const date = params['date'];
            const timeSlot = params['timeSlot'];
            this.patientId = patientId;
            this.calendarPhysicianId = params['physicianId'] || null;

            if (patientId && date) {
              // Use case 3: Patch the form with patientId and date
              setTimeout(() => {
                const offset = moment().utcOffset();
                this.patchForm({ patientId, date: moment.utc(date, 'YYYY-MM-DD').utcOffset(offset) });
                if (timeSlot) {
                  this.patchForm({ timeSlot });
                  this.calculateEndTime(timeSlot);
                }
                if (this.calendarPhysicianId) this.form.patchValue({ physicianId: this.calendarPhysicianId });
              })
            }
          });
      }
    });

    this.groupService.selectedGroup$
      .pipe(
        tap((selectedGroup) => {
          this.calendarSplitByPhysician = !!selectedGroup?.calendarSplitByPhysician;
          this.cd.markForCheck();
        }),
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.currentPatient, this.groupId) });
            this.loadTheatreRoster(this.groupId);
            return EMPTY;
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patientId = selectedPatient.id;
            // §3d — the chip selector takes `[patient]`; it reads speciality/BMI context off it
            // for the code prompts. The field was declared and never assigned until now.
            this.currentPatient = selectedPatient;
            this.form.patchValue({ patientId: this.patientId, groupId: patientGroupId(selectedPatient, this.groupId) });

            return EMPTY;
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.scheduleService.schedule$
      .pipe(
        switchMap(scheduleSettings => {
          if (scheduleSettings) {
            this.scheduleSettings = scheduleSettings;
            this.timeSlots = this.generateTimeSlots();
            return EMPTY;
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.groupUsers = (users || []).filter(
          (u: any) => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50',
        );
      });

    // HospitalService loads this itself off `selectedGroup$` — subscribing is the whole wiring.
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hospitals: any[]) => {
        this.hospitals = hospitals || [];
      });

    this.appointmentTypeService.appointmentTypes$
      .pipe(
        switchMap(appointmentTypes => {
          if (appointmentTypes) {
            this.appointmentTypes = appointmentTypes;
            this.refreshTypeOptions();
            if (!this.editingAppointment?.appointmentTypeId) {
              this.selectAppointmentType({ value: this.editingAppointment?.appointmentTypeId });
            }
            return EMPTY;
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.locationService.locations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((locations) => {
        this.locations = locations || [];
        this.applyLocations();
      });

    this.appointmentService.editingAppointment$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((appointment) => {
        this.editingAppointment = appointment;
        if (appointment) {
          this.patchForm(appointment);
          this.maybeLoadPlannedCodes();
        }
      });

    // Subscribe to value changes of the timeSlot control
    /*  this.form.get('timeSlot')!.valueChanges
       .pipe(takeUntil(this.destroy$))
       .subscribe(() => {
         this.calculateEndTime();
       }); */

    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }


  initForm() {
    this.isProcedure = false;
    this.suppressSending = false;
    this.notifyPatient = true;
    this.reminderChoice = null;
    this.applyTypeReminders(null);
    this.endSlotOverridden = false;

    this.form = this.fb.group({
      date: ['', Validators.required],
      timeSlot: ['', Validators.required],
      endSlot: ['', Validators.required],
      appointmentTypeId: ['', Validators.required],
      appointmentType: ['', Validators.required],
      procedure: [''],
      virtualLink: [''],
      starveTime: [''],
      arriveTime: [''],
      reasonForVisit: [''],
      patientId: [this.patientId, Validators.required],
      groupId: [patientGroupId(this.currentPatient, this.groupId), Validators.required],
      status: ['pending'], // Add status control
      physicianId: [''],
      // Procedure-only, and never required: a consultation has no hospital. Declared
      // unconditionally so `{ ...this.form.value }` in saveAppointment() carries it, and so
      // patchForm() can restore it when an existing procedure is reopened.
      hospitalId: [null],
      // Required only when the group has locations; applyLocations() sets that and the default.
      locationId: [null],

      // §3b — the theatre-list inputs. Same rule as `hospitalId`: declared unconditionally so
      // the spread in saveAppointment() carries them and patchForm() can restore them, hidden
      // by `*ngIf="isProcedure"` in the template, and CLEARED (not merely hidden) by
      // selectAppointmentType() when the booking stops being a procedure.
      //
      // Seeded null/'' and never anything else — see the no-copy-forward note on the class.
      anaesthetistColleagueId: [null],
      assistant1ColleagueId: [null],
      assistant2ColleagueId: [null],
      anaesthesiaType: [null],
      theatreNumber: [''],
      postOpWard: [''],
      specialInstructions: [''],
    }, {
      validator: this.validateEndSlot
    });
    this.applyLocations();
  }

  /**
   * Makes Location required when the group has locations, and starts a new booking on the location the calendar is
   * showing, or the default when it shows all. An existing appointment keeps what it has, even none.
   */
  private applyLocations() {
    const control = this.form?.get('locationId');
    if (!control) return;
    control.setValidators(this.locations.length ? Validators.required : null);
    if (this.locations.length && !this.editingAppointment?.id && !control.value) {
      control.setValue(this.calendarLocationFilter.getSelectedLocationId() ?? defaultLocationId(this.locations));
    }
    control.updateValueAndValidity({ emitEvent: false });
    this.refreshTypeOptions();
  }

  private refreshTypeOptions() {
    const keepTypeId = this.editingAppointment?.id ? this.editingAppointment.appointmentTypeId : null;
    this.typeOptions = typesForLocation(this.appointmentTypes, this.form?.get('locationId')?.value, this.locations, keepTypeId);
    // the types a chat's booking waits for may have just arrived
    this.matchChatType();
  }

  /** A new location narrows the types offered; a chosen type that is not offered there is cleared. */
  selectLocation(locationId: string | null) {
    this.form.patchValue({ locationId });
    this.refreshTypeOptions();
    const typeId = this.form.get('appointmentTypeId')?.value;
    if (typeId && !this.typeOptions.some((t) => t.id === typeId)) {
      this.form.patchValue({ appointmentTypeId: '' });
      this.selectAppointmentType({ value: '' });
    }
  }

  /**
   * §3b — the two booking pickers, filled from the roster endpoint rather than from the raw
   * colleague list.
   *
   * `GET /colleagues/roster/:groupId` filters on `Colleague.isAnaesthetist` /
   * `isSurgicalAssistant` SERVER-SIDE and returns `{ anaesthetists, surgicalAssistants }`. Doing
   * it there rather than here means the picker cannot drift from the settings panel that
   * populates it, and the practice's whole colleague network — which is mostly referrers, and
   * much larger — never has to be shipped to draw three dropdowns.
   *
   * The endpoint survived §3b's removal of the theatre-list surface and MOVED with it: it now
   * lives on `ColleagueService` / `colleagueController.loadTheatreRoster`, beside the other
   * colleague routes, because it reads `Colleagues` and has nothing to do with a theatre list.
   */
  private loadTheatreRoster(groupId: string): void {
    if (!groupId) return;
    this.colleagueService.getRoster(groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.anaesthetists = response?.data?.anaesthetists || [];
          this.surgicalAssistants = response?.data?.surgicalAssistants || [];
          this.cd.markForCheck();
        },
        error: () => {
          // An empty roster leaves the pickers empty and every other field working. Not worth a
          // toast: the settings panel that flags colleagues may simply not have been used yet.
          this.anaesthetists = [];
          this.surgicalAssistants = [];
        },
      });
  }

  /** Display name for a roster row — `Colleague` has no computed full name. */
  colleagueLabel(colleague: any): string {
    return [colleague?.title, colleague?.name, colleague?.surname].filter(Boolean).join(' ').trim();
  }

  /**
   * Drops every §3b theatre input. Called when the booking stops being a procedure; the fields
   * are hidden at that moment, and a hidden value is still spread into the save payload.
   */
  private clearTheatreFields(): void {
    this.form.patchValue({
      anaesthetistColleagueId: null,
      assistant1ColleagueId: null,
      assistant2ColleagueId: null,
      anaesthesiaType: null,
      theatreNumber: '',
      postOpWard: '',
      specialInstructions: '',
    });
  }

  messageTemplates = {
    confirmation: '',
    reschedule: '',
    cancellation: '',
    reminder: '',
    isProcedure: false,
  };

  hintPlaceholders = "{{patientName}}, {{groupName}}, {{appointmentDate}}, {{appointmentTime}}";

  validateEndSlot(formGroup: FormGroup) {
    const startTime = formGroup.get('timeSlot')?.value;
    const endTime = formGroup.get('endSlot')?.value;

    if (startTime && endTime) {
      const startMoment = moment(startTime, 'HH:mm');
      const endMoment = moment(endTime, 'HH:mm');

      if (endMoment.isSameOrBefore(startMoment)) {
        formGroup.get('endSlot')?.setErrors({ invalidEndSlot: true });
      } else {
        formGroup.get('endSlot')?.setErrors(null);
      }
    }

  }


  patchForm(appointment: any) {
    if (!appointment) {
      return; // Guard clause in case there's no appointment details
    }

    // Convert the date to a moment object if present
    if (appointment.date) {
      appointment.date = moment(appointment.date).format('YYYY-MM-DD');
    }

    // Determine current status and update status options
    this.currentStatus = this.determineCurrentStatus(appointment);
    this.statusOptions = this.getAvailableStatusOptions(appointment);

    // Patching form with appointment details
    this.form.patchValue({
      ...appointment,
      status: this.currentStatus
    });
    if (appointment.id) {
      this.form.patchValue({ locationId: appointment.locationId ?? null });
    }
    this.refreshTypeOptions();

    // Was the stored end DERIVED or TYPED? Nothing records it, so compare by value against what
    // the stored type would derive today. Only a full booking carries both slots — the partial
    // patches from the calendar handoff (`{ timeSlot }`, `{ patientId, date }`) skip this.
    //
    // If the types have not loaded yet the lookup misses, the schedule default is used, and a
    // type with an override is judged "typed". That is the conservative miss: a later type change
    // leaves the end alone rather than moving it, and nothing is destroyed.
    if (appointment.timeSlot && appointment.endSlot) {
      const storedType = this.appointmentTypes.find(at => at.id === appointment.appointmentTypeId);
      const derived = addMinutesToTime(appointment.timeSlot, effectiveDurationMinutes(storedType, this.scheduleSettings));
      this.endSlotOverridden = appointment.endSlot !== derived;
    }

    if (appointment.appointmentTypeId) {
      const appointmentType = this.appointmentTypes.find(at => at.id === appointment.appointmentTypeId);

      const patient = this.patientService.patient;
      const patientName = `${patient?.name} ${patient?.surname}`;
      const groupName = this.groupService.group?.name || '';
      const reminderMessage = appointmentType?.reminderMessage || '';
      const arriveLocation = appointmentType?.arriveLocation || 'The Treatment Centre';
      this.isProcedure = appointmentType?.isProcedure;
      this.suppressSending = appointmentType?.suppressSending === true;
      if ('sendReminder' in appointment) this.reminderChoice = appointment.sendReminder ?? null;
      this.applyTypeReminders(appointmentType);

      // Store the original date and time
      let originalDate = this.form.get('date')?.value;
      let originalTime = this.form.get('timeSlot')?.value;

      // Function to dynamically update messages
      const updateMessages = () => {
        const dateControl = this.form.get('date')?.value;
        const timeControl = this.form.get('timeSlot')?.value;
        const starveControl = this.form.get('starveTime')?.value;
        const arriveControl = this.form.get('arriveTime')?.value;

        const starveTime = starveControl ? `${moment(starveControl).format('HH:mm')}` : '';
        const arriveTime = arriveControl ? `${moment(arriveControl).format('HH:mm')}` : '';
        const appointmentDateTime = dateControl && timeControl
          ? `${moment(dateControl).format('YYYY-MM-DD')} at ${timeControl}`
          : '';

        // **Regex pattern to find and replace the old date and time in messages**
        const dateTimeRegex = /\d{4}-\d{2}-\d{2} at \d{2}:\d{2}/g;

        if (dateControl !== originalDate || timeControl !== originalTime) {
          if (appointment.rescheduleMessage) {
            appointment.rescheduleMessage = appointment.rescheduleMessage.replace(dateTimeRegex, appointmentDateTime);
          }

          if (appointment.autoReminderMessage) {
            appointment.autoReminderMessage = appointment.autoReminderMessage.replace(dateTimeRegex, appointmentDateTime);
          }

          if (appointment.cancellationMessage) {
            appointment.cancellationMessage = appointment.cancellationMessage.replace(dateTimeRegex, appointmentDateTime);
          }
        }

        this.messageTemplates = {
          confirmation: this.replacePlaceholders(
            appointment.confirmationMessage || appointmentType?.confirmationMessage || '',
            patientName,
            groupName,
            appointmentDateTime,
            reminderMessage,
            starveTime,
            arriveTime,
            arriveLocation
          ),
          reschedule: this.replacePlaceholders(
            appointment.rescheduleMessage || appointmentType?.rescheduleMessage || '',
            patientName,
            groupName,
            appointmentDateTime,
            reminderMessage,
            starveTime,
            arriveTime,
            arriveLocation
          ),
          cancellation: this.replacePlaceholders(
            appointment.cancellationMessage || appointmentType?.cancellationMessage || '',
            patientName,
            groupName,
            appointmentDateTime,
            reminderMessage,
            starveTime,
            arriveTime,
            arriveLocation
          ),
          reminder: this.replacePlaceholders(
            appointment.autoReminderMessage || appointmentType?.autoReminderMessage || '',
            patientName,
            groupName,
            appointmentDateTime,
            reminderMessage,
            starveTime,
            arriveTime,
            arriveLocation
          ),
          isProcedure: appointment.isProcedure,
        };

        // Update stored date and time
        originalDate = dateControl;
        originalTime = timeControl;
      };

      // Subscribe to changes in all relevant fields
      ['date', 'timeSlot', 'starveTime', 'arriveTime'].forEach(field => {
        const control = this.form.get(field);
        if (control) {
          control.valueChanges.subscribe(() => updateMessages());
        }
      });

      // Initialize messages with the current form values
      updateMessages();
    }
  }

  /** Opened from a chat's `/appointment`: the day, start time and reason it sent, and the conversation it came from. */
  private applyChatPrefill(): void {
    const prefill = appointmentPrefill(this.route.snapshot.queryParamMap);
    this.chatConversationId = prefill.chat;
    if (prefill.date) this.form.patchValue({ date: prefill.date });
    if (prefill.time) {
      this.form.patchValue({ timeSlot: prefill.time });
      this.onStartTimeSet(prefill.time);
    }
    if (prefill.text) this.form.patchValue({ reasonForVisit: prefill.text });
    this.chatTypeText = prefill.text || null;
    this.matchChatType();
  }

  /** The chat's text still to match against the appointment types (they may load after the prefill). */
  private chatTypeText: string | null = null;

  /**
   * A booking from the chat: the appointment type its text names ("follow-up" → Follow Up) is picked, and the rest of
   * the text stays as the reason. Once only, and never over a type the person has already chosen.
   */
  matchChatType(): void {
    const text = this.chatTypeText;
    if (!text || this.form.get('appointmentTypeId')?.value) return;
    const types = this.typeOptions?.length ? this.typeOptions : this.appointmentTypes;
    if (!types?.length) return;
    this.chatTypeText = null;
    // a type's name is its `type` field
    const match = closestAppointmentType(text, types.map((t: any) => ({ id: String(t.id), name: String(t.type ?? t.name ?? '') })));
    if (!match) return;
    this.form.patchValue({ appointmentTypeId: match.id, reasonForVisit: match.reason });
    this.selectAppointmentType({ value: match.id });
  }

  /** A booking made from a chat goes back to that conversation as a card, from the person who saved it. */
  private postChatCard(result: any): void {
    if (!this.chatConversationId) return;
    const booking = result?.data;
    const doctor = this.groupUsers.find((u: any) => booking?.physicianId && String(u.userId).toLowerCase() === String(booking.physicianId).toLowerCase());
    const doctorName = doctor ? [doctor.title, doctor.name, doctor.surname].filter(Boolean).join(' ') : null;
    const card = chatAppointmentCard(booking, this.currentPatient ?? { id: this.patientId }, doctorName);
    if (!card) return;
    this.chat.store.sendAction(this.chatConversationId, card).catch((e: unknown) => {
      console.error('Error posting the appointment to the chat: ', e);
      this.showToastMessage('The appointment was saved, but could not be posted to the chat.');
    });
  }

  resetForm() {
    setTimeout(() => {
      this.editingAppointment = null;
      this.form.reset();
      this.initForm();
      this.cd.detectChanges();
    });
  }

  /* ══ §25 — DOCUMENTS THIS BOOKING DID NOT SEND ═══════════════════════════════════════════
   *
   * A `skipped` row is terminal and blocks nothing (§25.2), so it is easy for it to be true and
   * invisible at the same time. §23.1 puts it HERE because the appointment is where a practice
   * asks "what was meant to go out for this visit" — and because the row already carries the
   * patient, the document and the physician, so "send it now" is the existing manual send.
   * ═══════════════════════════════════════════════════════════════════════════════════════ */

  /** One entry per skipped document raised by THIS appointment. Empty is the ordinary case. */
  skippedDocuments: { row: any; skip: DocumentSkipDescription }[] = [];

  /** The row currently being sent, so its button can be disabled without freezing the others. */
  sendingDocumentId: string | null = null;

  private loadSkippedDocuments(): void {
    const appointmentId = this.editingAppointment?.id;
    const patientId = this.editingAppointment?.patientId || this.patientId;
    if (!appointmentId || !patientId) return;

    this.patientDocumentSkipService.loadForAppointment(patientId, appointmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows: DocumentSkipRow[]) => {
          // Only the skipped ones. A `scheduled` or `sent` row belonging to this appointment is
          // not a problem and has its own home in the patient file's documents panel; repeating it
          // here would turn a warning strip into a second list nobody reads.
          this.skippedDocuments = (rows || [])
            .map((row: any) => ({ row, skip: describeDocumentSkip(row) }))
            .filter((entry): entry is { row: any; skip: DocumentSkipDescription } => !!entry.skip);
          this.cd.markForCheck();
        },
        // SILENT, deliberately. This is a warning strip on a booking screen: a failed read must not
        // put an error in front of a receptionist who came here to move an appointment.
        error: (error: any) => {
          console.error('Could not load this appointment\'s skipped documents', error);
          this.skippedDocuments = [];
        },
      });
  }

  documentNameFor(entry: { row: any }): string {
    return (entry.row && entry.row.PracticeDocument && entry.row.PracticeDocument.name) || 'Document';
  }

  /**
   * §23.1's send-now, from the appointment.
   *
   * Delivers inside the request rather than waiting for the next job run — "the practice may be
   * sending precisely because the appointment is tomorrow".
   */
  async sendSkippedDocument(entry: { row: any; skip: DocumentSkipDescription }): Promise<void> {
    if (!entry.skip.canSendNow || this.sendingDocumentId) return;

    const name = this.documentNameFor(entry);
    const confirmed = await this.showConfirmationDialog(
      'SEND THIS DOCUMENT NOW',
      `"${name}" was never sent for this appointment. Send it now? It goes out straight away, by `
      + `email and text message.`,
      undefined,
      'Cancel',
      'Send now',
    );
    if (!confirmed) return;

    this.sendingDocumentId = entry.row.id;
    this.patientDocumentSkipService.sendNow(entry.row.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendingDocumentId = null;
          this.showToastMessage(`"${name}" was sent to the patient.`);
          // Re-read rather than splicing the row out by hand: the server has just rewritten it,
          // and a screen that guesses at the new state is how two screens start disagreeing.
          this.loadSkippedDocuments();
        },
        error: (error: any) => {
          this.sendingDocumentId = null;
          const served = error?.error?.message || error?.message;
          this.showToastMessage(typeof served === 'string' && served
            ? served
            : `"${name}" could not be sent. Please try again.`);
          this.loadSkippedDocuments();
        },
      });
  }

  /* ══ §25.5 — THE BOOKING-TIME WARNING ════════════════════════════════════════════════════
   *
   * "The booking screen warns at save time when the patient has no reachable channel at all, so
   * the practice learns it then rather than discovering it days later."
   *
   * The failure it prevents is specific and quiet: the booking succeeds, the document rows are
   * raised `scheduled`, and days later the job marks them `skipped`/`unreachable` — correct
   * behaviour, discovered by a patient turning up without their forms. The patient is standing in
   * front of the receptionist NOW, which is the only moment the missing number is cheap to get.
   * ═══════════════════════════════════════════════════════════════════════════════════════ */

  /** No email address AND no mobile number: nothing this practice sends can reach them. */
  get patientUnreachable(): boolean {
    return patientHasNoChannel(this.currentPatient);
  }

  /**
   * Whether the warning is worth raising for THIS save.
   *
   * Only when notifications are on. With `notifyPatient` off — or a `suppressSending` type — the
   * documents are gated `skipped`/`suppressed` before reachability is ever consulted (§25.1), so a
   * warning about contact details would be describing a consequence that cannot happen, and a
   * warning that fires when nothing is at stake is one people learn to click through.
   */
  get shouldWarnUnreachable(): boolean {
    return this.patientUnreachable && this.notifyPatient && !this.suppressSending;
  }

  get notifyPatientLabel(): string {
    if (this.editingAppointment?.id) {
      return 'Notify patient about this change';
    }
    return 'Notify patient about this appointment';
  }

  isDateAvailable = (d: Date | null): boolean => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    if (!d) return false;
    // Convert Date to Moment for easier manipulation and comparison
    const dateMoment = moment(d);

    // Check if date is in the past

    /*  if (!dateMoment.isAfter(moment().subtract(1, 'days'))) {
       return false;
     } */

    if (!this.scheduleSettings) return true; // Allow all dates if no schedule settings are defined.

    const dayName = dayNames[dateMoment.day()];
    return this.scheduleSettings.availableDays.includes(dayName);
  }

  generateTimeSlots() {
    const slots: { value: string, viewValue: string }[] = [];
    if (!this.scheduleSettings) return slots;

    const { timeSlotDuration } = this.scheduleSettings;

    // Hardcoded start time at '06:00'
    let currentTime = moment('06:00', 'HH:mm');

    // End time 24 hours later, which will also be '06:00' but the next day
    const endTimeMoment = moment('06:00', 'HH:mm').add(1, 'days');

    while (currentTime < endTimeMoment) {
      slots.push({
        value: currentTime.format('HH:mm'),
        viewValue: currentTime.format('HH:mm')
      });
      currentTime.add(timeSlotDuration, 'minutes');
    }

    return slots;
  }

  /* generateTimeSlots() {
    const slots: { value: string, viewValue: string }[] = [];
    if (!this.scheduleSettings) return slots;
  
    const { startTime, endTime, timeSlotDuration } = this.scheduleSettings;
    let currentTime = moment(startTime, 'HH:mm');
    const endTimeMoment = moment(endTime, 'HH:mm');
  
    while (currentTime < endTimeMoment) {
      slots.push({
        value: currentTime.format('HH:mm'),
        viewValue: currentTime.format('hh:mm A')
      });
      currentTime.add(timeSlotDuration, 'minutes');
    }
  
    return slots;
  } */

  /**
   * The type currently chosen on the form, or undefined before one is. Drives the derived end.
   */
  private selectedAppointmentType(): any | undefined {
    const appointmentTypeId = this.form.get('appointmentTypeId')?.value;
    return appointmentTypeId ? this.appointmentTypes.find(a => a.id === appointmentTypeId) : undefined;
  }

  /**
   * Derives End Time = `startTime` + the chosen type's effective duration (type override, else
   * the schedule's `timeSlotDuration`, else 30). Called from the Start Time picker, from the
   * calendar handoff, and from selectAppointmentType() — the last only while the end is still
   * derived (`endSlotOverridden` false).
   */
  calculateEndTime(startTime: string): void {
    if (!startTime) { return; }
    const minutes = effectiveDurationMinutes(this.selectedAppointmentType(), this.scheduleSettings);
    this.form.get('endSlot')?.setValue(addMinutesToTime(startTime, minutes));
  }

  /** Start Time picker. A new start means the end is derived again — see `endSlotOverridden`. */
  onStartTimeSet(startTime: string): void {
    this.endSlotOverridden = false;
    this.calculateEndTime(startTime);
  }

  /** End Time picker. From here on the end is the user's until they move the start. */
  onEndTimeSet(): void {
    this.endSlotOverridden = true;
  }


  isSlotConsideredFull(conflicts: any[], scheduleSettings: any): boolean {
    return shouldWarnOfConflict(conflicts, scheduleSettings);
  }

  /** The switch follows the type, unless this booking has its own choice. */
  private applyTypeReminders(type: any): void {
    this.typeSendsReminders = type?.sendReminders !== false;
    this.sendReminder = this.reminderChoice ?? this.typeSendsReminders;
  }

  /** The switch: a choice that matches the type is not kept, so the booking goes on following it. */
  setSendReminder(value: boolean): void {
    this.sendReminder = value;
    this.reminderChoice = value === this.typeSendsReminders ? null : value;
  }

  selectAppointmentType(event: MatSelectChange | { value: string }) {
    const appointmentTypeId = event.value;

    // Get the selected appointment type from the available types
    const selectedType = this.appointmentTypes.find(a => a.id === appointmentTypeId);

    this.isProcedure = selectedType?.isProcedure;
    this.suppressSending = selectedType?.suppressSending === true;
    this.applyTypeReminders(selectedType);
    // §23.1.1 — the type's document links, for the save-time prompt. Here rather than at save,
    // so pressing Save never waits on a round trip. The picker is about to re-load against the
    // new type, so anything it said about the previous one is stale.
    this.documentSelectionKnown = false;
    this.loadLateDocumentLinks(this.form.get('appointmentTypeId')?.value);

    // Patch the form value for the appointment type
    this.form.patchValue({ appointmentType: selectedType?.type });

    // The Hospital select is hidden for a non-procedure, so a leftover value would be invisible
    // AND still saved — switching a booking from theatre to a consultation must drop it.
    if (!this.isProcedure && this.form.get('hospitalId')?.value) {
      this.form.patchValue({ hospitalId: null });
    }

    // §3b — same reasoning for the theatre-list inputs, and it matters more here: an
    // anaesthetist's name left on a booking that is no longer a procedure is a wrong name on
    // a document, and the whole point of the no-copy-forward rule is that a name is only ever
    // present because somebody put it there for THIS case.
    if (!this.isProcedure) {
      this.clearTheatreFields();
    }

    // §3d — a theatre type was chosen, so this booking has procedure codes to show. Switching
    // back to a consultation empties the selector; the SAVE is what clears them server-side,
    // via the same replace-set that writes them, so nothing is destroyed by browsing types.
    if (this.isProcedure) {
      this.loadPlannedCodesForBooking();
    } else {
      this.plannedCodes = [];
      this.motivationBaseline = null;
    }

    if (this.calendarSplitByPhysician && selectedType?.defaultPhysicianId && !this.calendarPhysicianId) {
      this.form.patchValue({ physicianId: selectedType.defaultPhysicianId });
    }

    // The type's duration sets the end time — unless the user has typed one. The common path
    // from the calendar arrives with the start already set and the end derived from the schedule
    // default; this is the moment it is corrected to the type's own length.
    const currentStart = this.form.get('timeSlot')?.value;
    if (currentStart && !this.endSlotOverridden) {
      this.calculateEndTime(currentStart);
    }

    // Ensure 'date' and 'timeSlot' controls are defined
    const dateControl = this.form.get('date');
    const timeSlotControl = this.form.get('timeSlot');

    // Check if the selected appointment type requires procedure-specific fields
    if (selectedType?.isProcedure && dateControl && timeSlotControl) {
      // Ensure 'starveTime' and 'arriveTime' controls exist in the form
      const starveControl = this.form.get('starveTime');
      const arriveControl = this.form.get('arriveTime');
      const arriveLocation = selectedType.arriveLocation || "The Treatment Centre";

      if (starveControl && arriveControl) {
        // Combine value changes for relevant controls
        combineLatest([
          dateControl.valueChanges.pipe(startWith(dateControl.value)),  // Ensure initial value is emitted
          timeSlotControl.valueChanges.pipe(startWith(timeSlotControl.value)),  // Ensure initial value is emitted
          starveControl.valueChanges.pipe(startWith(starveControl.value)),  // Ensure initial value is emitted
          arriveControl.valueChanges.pipe(startWith(arriveControl.value))  // Ensure initial value is emitted
        ]).subscribe(([date, timeSlot, starveTime, arriveTime]) => {
          // Ensure all required values are selected
          if (date && timeSlot) {
            // Format the appointment date and time
            const appointmentDateTime = `${moment(date).format('YYYY-MM-DD')} at ${timeSlot}`;
            const formattedStarveTime = `${starveTime}`;
            const formattedArriveTime = `${arriveTime}`;
            // Set default values for patient and group info once
            const patientName = `${this.patientService.patient?.name} ${this.patientService.patient?.surname}` || 'patient';
            const groupName = this.groupService.group?.name || 'Group';
            const reminderMessage = selectedType?.reminderMessage || '';


            // Call setMessages with the necessary data
            this.setMessages(
              appointmentTypeId,
              patientName,
              groupName,
              appointmentDateTime,
              reminderMessage,
              formattedStarveTime,
              formattedArriveTime,
              arriveLocation,
            );
          }
        });
      } else {
        console.warn('StarveTime or ArriveTime form controls are missing for a procedure type.');
      }
    } else if (dateControl && timeSlotControl) {
      // Handle non-procedure appointment types
      combineLatest([
        dateControl.valueChanges.pipe(startWith(dateControl.value)),
        timeSlotControl.valueChanges.pipe(startWith(timeSlotControl.value))
      ]).subscribe(([date, timeSlot]) => {
        if (date && timeSlot) {
          const appointmentDateTime = `${moment(date).format('YYYY-MM-DD')} at ${timeSlot}`;
          const patientName = `${this.patientService.patient?.name} ${this.patientService.patient?.surname}` || 'patient';
          const groupName = this.groupService.group?.name || 'Group';
          const reminderMessage = selectedType?.reminderMessage || '';
          const startTime = "";
          const arriveTime = "";
          const arriveLocation = "";

          // Call setMessages without procedure-specific details
          this.setMessages(
            appointmentTypeId,
            patientName,
            groupName,
            appointmentDateTime,
            reminderMessage,
            startTime,
            arriveTime,
            arriveLocation,
          );
        }
      });
    } else {
      console.warn('Date or TimeSlot form controls are missing.');
    }
  }


  replacePlaceholders(template: string, patientName: string, groupName: string, appointmentDateTime: string, reminderMessage: string, starveTime: string, arriveTime: string, arriveLocation: string): string {
    return fillMessagePlaceholders(template, { patientName, groupName, appointmentDateTime, reminderMessage, starveTime, arriveTime, arriveLocation });
  }



  setMessages(
    appointmentTypeId: string,
    patientName: string,
    groupName: string,
    appointmentDateTime: string,
    reminderMessage: string,
    starveTime: string,
    arriveTime: string,
    arriveLocation: string,
  ) {
    const appointmentType = this.appointmentTypes.find(at => at.id === appointmentTypeId);

    if (!appointmentType) {
      console.error('Appointment type not found for ID:', appointmentTypeId);
      return;
    }
    if (appointmentType.isProcedure) {
      this.messageTemplates = {
        confirmation: starveTime && arriveTime ? appointmentType.confirmationMessage : appointmentType.trunConfirmationMessage,
        reschedule: starveTime && arriveTime ? appointmentType.rescheduleMessage : appointmentType.trunRescheduleMessage,
        cancellation: starveTime && arriveTime ? appointmentType.cancellationMessage : appointmentType.trunCancellationMessage,
        reminder: starveTime && arriveTime ? appointmentType.autoReminderMessage : appointmentType.trunReminderMessage,
        isProcedure: appointmentType.isProcedure,
      };
    } else {
      this.messageTemplates = {
        confirmation: reminderMessage ? appointmentType.confirmationMessage : appointmentType.trunConfirmationMessage,
        reschedule: reminderMessage ? appointmentType.rescheduleMessage : appointmentType.trunRescheduleMessage,
        cancellation: reminderMessage ? appointmentType.cancellationMessage : appointmentType.trunCancellationMessage,
        reminder: reminderMessage ? appointmentType.autoReminderMessage : appointmentType.trunReminderMessage,
        isProcedure: appointmentType.isProcedure,
      };
    }

    // Fetch default messages if not set
    this.messageTemplates.confirmation ??= appointmentType.isProcedure
      ? DEFAULT_EMAILS.THEATRE_CONFIRMATION
      : DEFAULT_EMAILS.CONFIRMATION;

    this.messageTemplates.reschedule ??= appointmentType.isProcedure
      ? DEFAULT_EMAILS.THEATRE_RESCHEDULE
      : DEFAULT_EMAILS.RESCHEDULE;

    this.messageTemplates.cancellation ??= appointmentType.isProcedure
      ? DEFAULT_EMAILS.THEATRE_CANCELLATION
      : DEFAULT_EMAILS.CANCELLATION;

    this.messageTemplates.reminder ??= appointmentType.isProcedure
      ? DEFAULT_EMAILS.THEATRE_REMINDER
      : DEFAULT_EMAILS.REMINDER;

    // Replace placeholders with actual values
    this.messageTemplates.confirmation = this.replacePlaceholders(
      this.messageTemplates.confirmation,
      patientName,
      groupName,
      appointmentDateTime,
      reminderMessage,
      starveTime,
      arriveTime,
      arriveLocation
    );
    this.messageTemplates.reschedule = this.replacePlaceholders(
      this.messageTemplates.reschedule,
      patientName,
      groupName,
      appointmentDateTime,
      reminderMessage,
      starveTime,
      arriveTime,
      arriveLocation
    );
    this.messageTemplates.cancellation = this.replacePlaceholders(
      this.messageTemplates.cancellation,
      patientName,
      groupName,
      appointmentDateTime,
      reminderMessage,
      starveTime,
      arriveTime,
      arriveLocation
    );
    this.messageTemplates.reminder = this.replacePlaceholders(
      this.messageTemplates.reminder,
      patientName,
      groupName,
      appointmentDateTime,
      reminderMessage,
      starveTime,
      arriveTime,
      arriveLocation
    );
  }



  /**
   * The patient this booking is for. `patientId` is populated from `currentPatient$` or the
   * query params for a new booking; an appointment opened for edit carries its own.
   */
  private get bookingPatientId(): string | null {
    return this.editingAppointment?.patientId || this.patientId || this.form?.get('patientId')?.value || null;
  }

  /**
   * §3d. Fills the selector for a booking that has just become (or was opened as) a procedure.
   *
   * Two sources, in this order, and the order is the whole rule:
   *
   *   1. **What this booking already plans.** An appointment opened for edit reads its own
   *      codes. They win outright — the booking is the most recent statement of intent, and a
   *      motivation must never overwrite a list somebody has already curated here.
   *   2. **An unconsumed motivation**, only when (1) is empty. "Unconsumed" is decided by the
   *      server and by three columns rather than by a date: `active = true`,
   *      `stage = 'motivated'`, `appointmentId IS NULL`.
   *
   * ONE motivation prefills. TWO OR MORE prefill nothing and are offered one at a time — see
   * the §3d block on the class for why that asymmetry is the point rather than an omission.
   */
  private async loadPlannedCodesForBooking(): Promise<void> {
    if (this.plannedCodesInFlight) return;
    const patientId = this.bookingPatientId;
    if (!patientId) return;

    // Once per booking. See `plannedCodesLoadedForKey` — a second load would re-prefill over
    // the user's own edits.
    const key = this.editingAppointment?.id || 'new';
    if (this.plannedCodesLoadedForKey === key) return;

    this.plannedCodesInFlight = true;
    try {
      // ── 1. What this booking already plans ────────────────────────────────────────
      if (this.editingAppointment?.id) {
        const existing = await this.plannedProcedureCodeService
          .loadByAppointment(this.editingAppointment.id)
          .pipe(takeUntil(this.destroy$))
          .toPromise();

        const rows = (existing && Array.isArray(existing.data)) ? existing.data : [];
        // `plannedCodesLoaded` is set even for an empty answer: "this booking plans nothing"
        // is a fact we now hold, and the save is entitled to act on it.
        this.plannedCodesLoaded = true;
        this.plannedCodesLoadedForKey = key;
        if (rows.length > 0) {
          this.plannedCodes = rows.map((row) => plannedChip(row.code, row.description, row.quantity));
          // A code already on the booking may have come off a letter, but this screen did not
          // put it there in this session, so there is no prefill to have drifted from and no
          // baseline to warn against. The server still knows the provenance; the marker is
          // about what happened in front of the user.
          this.motivationBaseline = null;
          this.cd.markForCheck();
          return;
        }
      } else {
        // A new booking plans nothing until this screen says otherwise.
        this.plannedCodesLoaded = true;
        this.plannedCodesLoadedForKey = key;
      }

      // ── 2. An unconsumed motivation ───────────────────────────────────────────────
      const response = await this.plannedProcedureCodeService.loadUnconsumedMotivations(patientId)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      const motivations: UnconsumedMotivation[] = (response && Array.isArray(response.data)) ? response.data : [];
      const usable = motivations.filter((m) => m && m.sourceAiRequestId
        && Array.isArray(m.codes) && m.codes.some((c) => !!c?.code));

      if (usable.length === 0) return;

      if (usable.length === 1) {
        this.prefillFromMotivation(usable[0]);
        return;
      }

      // MORE THAN ONE. Picking is inference; offering is not. One yes/no each, in the order
      // the server returned them, and the first acceptance prefills and stops.
      for (const motivation of usable) {
        if (this.offeredMotivationAiRequestIds.has(motivation.sourceAiRequestId)) continue;
        this.offeredMotivationAiRequestIds.add(motivation.sourceAiRequestId);

        // Codes only, no descriptions — tariff text runs to ~190 characters a line and turns a
        // yes/no prompt into a wall people dismiss unread. The full list with descriptions is
        // on the screen behind the dialog the moment they accept.
        const summary = motivation.codes.map((c) => c?.code).filter(Boolean).join(', ');
        const when = motivation.motivatedAt ? moment(motivation.motivatedAt).format('D MMM YYYY') : 'an earlier date';

        const accept = await this.showConfirmationDialog(
          'WHICH MOTIVATION IS THIS CASE FOR?',
          `This patient has ${usable.length} letters of motivation with no booking. `
          + `Use the one from ${when} (codes ${summary})?`,
          undefined,
          'Not this one',
          'Use these codes'
        );
        if (!accept) continue;

        this.prefillFromMotivation(motivation);
        return;
      }
    } catch (error) {
      // Silent by design. Failing to PREFILL is a missing convenience — the selector is still
      // there and still typeable, and a dialog raised by an error would be noise on a screen
      // whose job is booking the appointment.
      //
      // `plannedCodesLoaded` is deliberately NOT set on this path. If the load failed we do not
      // know what this booking plans, and a save must not overwrite the grid with a guess.
      console.error('Could not load the planned procedure codes for this booking', error);
    } finally {
      this.plannedCodesInFlight = false;
      this.cd.markForCheck();
    }
  }

  /**
   * The load, gated on the booking actually being a procedure.
   *
   * Called from every place that can newly reveal a procedure booking — the route load, the
   * `editingAppointment$` stream and the type picker — because none of them is guaranteed to
   * run, or to run first. `loadPlannedCodesForBooking` is idempotent per booking, which is what
   * makes calling it from three places safe rather than merely tolerable.
   */
  private maybeLoadPlannedCodes(): void {
    if (!this.isProcedure) return;
    this.loadPlannedCodesForBooking();
  }

  /** Puts a motivation's codes in the selector and records what they were, for the marker. */
  private prefillFromMotivation(motivation: UnconsumedMotivation): void {
    const codes = (motivation.codes || []).filter((c) => !!c?.code);
    this.plannedCodes = codes.map((c) => plannedChip(c.code, c.description, c.quantity));
    this.motivationBaseline = {
      sourceAiRequestId: motivation.sourceAiRequestId,
      motivatedAt: motivation.motivatedAt,
      codes: codes.map((c) => this.normaliseCode(c.code)),
    };
    this.cd.markForCheck();
  }

  /** Emitted by the chip selector. The whole set, every time. */
  onPlannedCodesChanged(codes: ProcedureCode[]): void {
    this.plannedCodes = Array.isArray(codes) ? codes : [];
  }

  /**
   * Same normalisation the server's `utils/codingDivergence.js` uses. Trimmed and upper-cased,
   * never numeric: `0008` and `8` are different tariff codes and `Number()` collapses them.
   */
  private normaliseCode(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim().toUpperCase();
  }

  /** Human-readable date of the letter the codes were prefilled from. */
  get motivationBaselineDate(): string {
    if (!this.motivationBaseline?.motivatedAt) return '';
    return moment(this.motivationBaseline.motivatedAt).format('D MMM YYYY');
  }

  /**
   * §3d — the drift marker. What has changed since the letter's codes were prefilled.
   *
   * DELIBERATELY PASSIVE, and this is a decision rather than an oversight. At this moment the
   * user is editing a list we prefilled thirty seconds ago; a modal telling them their own
   * edit differs from the prefill is a warning about the thing they just did on purpose, and
   * it is how a warning gets trained away. The interrupting warning is at NOTE SAVE, where the
   * two facts were entered at different times and possibly by different people.
   *
   * Null when there is nothing to say — no letter to compare against, or nothing changed.
   */
  get motivationDrift(): { added: string[]; removed: string[] } | null {
    const baseline = this.motivationBaseline;
    if (!baseline) return null;

    // Multiset, not set: a booking may legitimately plan the same code twice (two lesions, one
    // code), and dropping one of the two is a real removal that a Set would hide.
    const remaining = [...baseline.codes];
    const added: string[] = [];
    for (const chip of this.plannedCodes || []) {
      const code = this.normaliseCode((chip as any)?.code);
      if (!code) continue;
      const at = remaining.indexOf(code);
      if (at >= 0) remaining.splice(at, 1);
      else added.push(code);
    }

    if (added.length === 0 && remaining.length === 0) return null;
    return { added, removed: remaining };
  }

  /**
   * §3d — writes the selector's contents to the booking. REPLACE-SET: what is on screen is what
   * the booking plans, so removing every chip legitimately clears it.
   *
   * Deliberately NOT piped through `takeUntil(this.destroy$)`: this is called from the save
   * handler, and `returnToParent()` destroys the component moments later. A request cancelled
   * in that gap would leave the appointment saved and its codes stale — the silent half-write
   * this whole task exists to avoid. It always resolves; the caller navigates away either way.
   *
   * ── THE GUARD THAT MATTERS ───────────────────────────────────────────────────────
   *
   * Nothing is sent unless `plannedCodesLoaded` is true. Saving an existing theatre booking
   * before its codes arrived — or after the load failed — would otherwise send an empty
   * replace-set and clear the hospital's grid without a word.
   */
  private async savePlannedCodes(appointmentId: string | undefined | null): Promise<void> {
    if (!appointmentId || !this.plannedCodesLoaded || !this.showPlannedCodes) return;

    // A booking that is no longer a procedure plans nothing. Same reasoning as
    // `clearTheatreFields()`: a value left behind on a booking it does not describe is worse
    // than an empty one, and here it would print on a hospital document.
    const codes: PlannedCodeInput[] = this.isProcedure
      ? (this.plannedCodes || [])
        .filter((chip: any) => chip && typeof chip.code === 'string' && chip.code.trim())
        .map((chip: any) => ({
          code: String(chip.code).trim(),
          description: chip.description || null,
          quantity: Number.isFinite(Number(chip.quantity)) && Number(chip.quantity) > 0 ? Number(chip.quantity) : 1,
        }))
      : [];

    try {
      const result = await this.plannedProcedureCodeService
        .replaceForAppointment(appointmentId, codes, this.motivationBaseline?.sourceAiRequestId || null)
        .toPromise();

      const saved = (result && Array.isArray(result.data)) ? result.data.length : 0;
      if (saved > 0) {
        this.showToastMessage(`${saved} procedure code${saved === 1 ? '' : 's'} linked to this booking`);
      }
    } catch (error) {
      console.error('Failed to save the planned procedure codes for this appointment', error);
      this.showToastMessage('The appointment was saved but its procedure codes could not be updated.');
    }
  }

  /**
   * §23.1.1 — the links the practice was PROMPTED about and ACCEPTED, for this save only.
   *
   * Rebuilt on every save attempt and cleared after it. Holding it across attempts would mean a
   * receptionist who accepted, then changed the date to one where nothing is late, still sends a
   * `send_now` for a link that is no longer late.
   */
  private acceptedLateDecisions: LateDocumentDecision[] = [];

  /**
   * §23.1.1 — the chosen type's document links, held for the save-time prompt.
   *
   * Fetched when the type changes rather than when Save is pressed, so the prompt costs no
   * latency at the one moment latency is least acceptable. Empty is a valid state and means
   * "ask nothing": no type chosen yet, an edit, a read still in flight, or a read that failed.
   */
  private lateDocumentLinks: LateDocumentLink[] = [];

  /**
   * Read the chosen type's links, for the late-document prompt.
   *
   * Only for a NEW booking: an edit raises no documents (`raiseAppointmentDocumentRequests` is
   * called from `addPatientAppointment` and nowhere else), so there is nothing to be late about.
   *
   * Never throws and never reports — the service already collapses a failure to `[]`. A trigger
   * read is not something a receptionist can act on, and a toast about one would be noise in the
   * middle of a booking.
   */
  private loadLateDocumentLinks(appointmentTypeId: string | null | undefined): void {
    this.lateDocumentLinks = [];
    if (!appointmentTypeId || this.editingAppointment) return;

    this.documentAttachmentService.loadAppointmentTypeLinks(
      String(appointmentTypeId), this.groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((links) => { this.lateDocumentLinks = links || []; });
  }

  /**
   * §23.1.1 — ASK BEFORE SAVING when this booking is already too late to send a document.
   *
   * A document linked "2 weeks before" on an appointment booked in 2 days names a day that has
   * gone. The send job fires on the day the RULE names, so that ask would never go out — and
   * before this prompt existed the only trace was a `skipped` banner on the patient file,
   * discovered later by somebody who was not the person who made the booking.
   *
   * ── WHY IT ONLY RUNS ON A NEW BOOKING ────────────────────────────────────────────────────
   *
   * `raiseAppointmentDocumentRequests` is called from `addPatientAppointment` and nowhere else, so
   * an edit raises no documents and has nothing to be late about. It is the same condition that
   * gates `practiceDocumentIds` a few lines below.
   *
   * ── WHY SUPPRESSION SKIPS IT ENTIRELY ────────────────────────────────────────────────────
   *
   * §25.1: an appointment type marked `suppressSending`, or a booking with the notify toggle off,
   * is an instruction that this patient is not contacted about this booking AT ALL. The server
   * gates the document on both and raises `skipped`/`suppressed` regardless of any decision, so
   * prompting would be offering a choice that has already been made — and answering "yes" to it
   * would change nothing, which is worse than not asking.
   *
   * ── IT CANNOT BLOCK THE BOOKING, AND IT DOES NO I/O ──────────────────────────────────────
   *
   * The links are fetched when the TYPE is chosen (`loadLateDocumentLinks`), never here. A save
   * that awaited a round trip would leave a receptionist looking at an unresponsive button at the
   * exact moment they are trying to book a patient in front of them — and on a slow connection
   * that wait is unbounded.
   *
   * So a read that has not landed yet, or failed, simply leaves this list empty and nothing is
   * asked. The server measures lateness independently and still marks the row, so the worst case
   * is the behaviour that shipped before this prompt existed: a visible `skipped` row with a
   * Send-now button on the patient file.
   *
   * Returns false only when the practice actively cancels the save.
   */
  private async confirmLateDocuments(): Promise<boolean> {
    this.acceptedLateDecisions = [];

    if (this.editingAppointment) return true;
    if (this.suppressSending || this.notifyPatient === false) return true;

    const appointmentTypeId = this.form.get('appointmentTypeId')?.value;
    const rawDate = this.form.get('date')?.value;
    if (!appointmentTypeId || !rawDate) return true;

    const decisions = lateDocumentDecisions(
      // Loaded when the TYPE was chosen, not now. See `loadLateDocumentLinks`.
      this.lateDocumentLinks,
      moment(rawDate).format('YYYY-MM-DD'),
      new Date(),
      // The picker's selection, honoured exactly as the server honours `practiceDocumentIds`:
      // null is "no picker ran", which is NOT an empty selection.
      // Filter by the picker's selection ONLY once the picker has actually emitted one — see
      // `documentSelectionKnown`. Until then the links decide, which is also what the server
      // does with an absent `practiceDocumentIds`.
      this.documentSelectionKnown ? (this.selectedDocumentIds || []) : null,
    );
    if (!decisions.length) return true;

    const many = decisions.length > 1;
    const accepted = await this.showConfirmationDialog(
      many ? 'THESE DOCUMENTS ARE ALREADY OVERDUE' : 'THIS DOCUMENT IS ALREADY OVERDUE',
      `This appointment is sooner than ${many ? 'these documents are' : 'this document is'} `
      + `normally sent. ${many ? 'Their' : 'Its'} send ${many ? 'days have' : 'day has'} already `
      + `passed, so ${many ? 'they' : 'it'} will NOT be sent at all unless you send `
      + `${many ? 'them' : 'it'} now.`,
      lateDocumentLines(decisions),
      // The cancel text is the honest description of what declining does — not "Cancel", which
      // reads as "abandon the booking".
      many ? 'Do not send them' : 'Do not send it',
      many ? 'Send them now' : 'Send it now',
    );

    // BOTH answers save the appointment. The question is about the DOCUMENTS, and a receptionist
    // who does not want a two-week-old questionnaire going out today still wants the booking.
    if (accepted) this.acceptedLateDecisions = decisions;
    return true;
  }

  async saveAppointment() {
    // A save is already in flight — ignore the repeat click instead of firing a second POST.
    if (this.isSaving) return;

    const { confirmation, reschedule, cancellation, reminder } = this.messageTemplates;
    if (!confirmation || !reschedule || !cancellation || !reminder) {
      this.showToastMessage('One or more message templates are missing. Please ensure all necessary fields are filled.');
      return;
    }

    /* §25.5 — THE PATIENT CANNOT BE REACHED, AND THIS IS THE MOMENT TO SAY SO.
     *
     * BEFORE `isSaving` is set, so a cancelled dialog leaves the button usable — and before the
     * slot-availability round trip, so the receptionist is not asked two questions in a row.
     *
     * It WARNS, it does not refuse. A booking is a clinical fact and a missing mobile number is
     * not a reason to turn a patient away; §4's rule that a document failure must never fail a
     * booking is the same principle one screen earlier. "Save anyway" is a real answer. */
    if (this.shouldWarnUnreachable) {
      const proceed = await this.showConfirmationDialog(
        'THIS PATIENT CANNOT BE CONTACTED',
        'This patient has no email address and no mobile number on file, so nothing this practice '
        + 'sends can reach them — no confirmation, no reminder, and no documents this appointment '
        + 'type is set up to send. Add contact details to the patient to fix it. You can still '
        + 'save the appointment.',
        undefined,
        'Add contact details first',
        'Save anyway',
      );
      if (!proceed) return;
    }

    // §23.1.1. AFTER the reachability warning and BEFORE `isSaving`, following the shape the
    // warning above set: a question that can cancel the save must be asked while the form is
    // still interactive.
    if (!(await this.confirmLateDocuments())) return;

    this.isSaving = true;

    try {
      const { date, timeSlot, endSlot } = this.form.value;
      // Bare calendar day, the same form the save below persists — an ISO timestamp never
      // matched those rows, so the conflict warning silently stopped firing.
      const formattedDate = moment(date).format('YYYY-MM-DD');
      const id = this.editingAppointment?.id || '';
      const physicianIdForSlot = this.calendarSplitByPhysician ? (this.form.get('physicianId')?.value || null) : undefined;
      const appointmentsResponse = await this.appointmentService.checkAppointmentSlotAvailability(
        formattedDate,
        formattedDate,
        timeSlot,
        endSlot,
        this.groupId,
        id,
        physicianIdForSlot,
      ).toPromise();
      const appointments = appointmentsResponse.data;

      if (this.isSlotConsideredFull(appointments, this.scheduleSettings)) {
        const proceedWithBooking = await this.showConfirmationDialog('SCHEDULING CONFLICT', 'Appointments/Events already exist for this slot. Do you want to continue anyway?', appointments);
        if (!proceedWithBooking) {
          this.isSaving = false;
          return;
        }
      }

      const appointmentData = { ...this.form.value };
      // Always persist a plain local calendar day. The picker hands back a Date, which
      // serialised as a UTC timestamp and made every later edit look like a reschedule.
      if (appointmentData.date) {
        appointmentData.date = moment(appointmentData.date).format('YYYY-MM-DD');
      }
      if (!appointmentData.physicianId) {
        appointmentData.physicianId = null;
      }
      // Pass the message templates when calling the appointment service methods
      appointmentData.messages = this.messageTemplates; // Add this line to pass the messages to the server
      appointmentData.notifyPatient = this.notifyPatient; // Include notify patient flag
      appointmentData.sendReminder = this.reminderForSave;
      /*
       * §30. The picker's selection, on a NEW booking only.
       *
       * Sent only when this is a new appointment, because that is the only path that renders the
       * picker and the only one that raises documents — `updateAppointment` does not. An edit
       * carrying the field would tell the server a user made a choice on a screen that never
       * offered them one.
       *
       * ABSENT vs EMPTY: `undefined` means the type's triggers decide, `[]` means the user
       * unticked everything.
       */
      if (!this.editingAppointment) {
        // A state unit sends no documents: an empty choice, never the type's triggers.
        appointmentData.practiceDocumentIds = this.stateMode ? [] : this.selectedDocumentIds;
        /*
         * §23.1.1. The links the practice was asked about and accepted, and ONLY those — the
         * server treats an id it did not itself return as deciding nothing, so a stale body
         * cannot make an unrelated document due.
         *
         * Absent when nothing was late or the offer was declined, which is the same body every
         * caller sent before this prompt existed. Declining is not "send nothing": the server
         * still raises the row as `skipped`/`late`, which is what puts the Send-now banner on the
         * patient file.
         */
        if (this.acceptedLateDecisions.length) {
          appointmentData.documentDecisions = sendNowDecisions(this.acceptedLateDecisions);
        }
      }

      // Include status-related fields for existing appointments
      if (this.editingAppointment?.id) {
        const appointment = this.editingAppointment;
        appointmentData.patientArrivedTime = appointment.patientArrivedTime;
        appointmentData.startTime = appointment.startTime;
        appointmentData.endTime = appointment.endTime;
        appointmentData.isCancelled = appointment.isCancelled;
        appointmentData.didNotAttend = appointment.didNotAttend;
        appointmentData.noteSaved = appointment.noteSaved;

        // Update existing appointment
        this.appointmentService.updateAppointment(this.editingAppointment.id, appointmentData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: async (result) => {
              // Leave isSaving set — returnToParent() navigates away and destroys the component.
              this.showToastMessage('Patient appointment updated successfully');
              // §3d — write the codes BEFORE navigating. returnToParent() destroys this
              // component, and a request fired into that gap is cancelled with the appointment
              // already saved and its codes stale.
              await this.savePlannedCodes(this.editingAppointment?.id);
              this.returnToParent();
            },
            error: (error) => {
              this.isSaving = false;
              console.error('Error updating patient appointment: ', error);
              this.showToastMessage('There was an error updating the patient appointment. Please try again.');
            }
          });
      } else {
        // Schedule new appointment
        this.appointmentService.bookAppointment(appointmentData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: async (result) => {
              // Leave isSaving set — returnToParent() navigates away and destroys the component.
              this.showToastMessage('Patient appointment saved successfully');
              this.usage.flow('appointment', 'done');
              this.postChatCard(result);
              // §3d — the new appointment's id exists for the first time HERE. That is the whole
              // reason the codes are held in the component and written afterwards rather than
              // posted with the booking: until this line there is nothing to key them on.
              await this.savePlannedCodes(result?.data?.id);
              this.returnToParent();
            },
            error: (error) => {
              this.isSaving = false;
              console.error('Error saving patient appointment: ', error);
              this.showToastMessage('There was an error saving the patient appointment. Please try again.');
            }
          });
      }
    } catch (error) {
      // Slot-availability check or conflict dialog failed — release the button so the user can retry.
      this.isSaving = false;
      console.error('Error saving patient appointment: ', error);
      this.showToastMessage('There was an error saving the patient appointment. Please try again.');
    }
  }

  async deleteAppointment() {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    const deleteData: any = {
      notifyPatient: this.notifyPatient
    };

    // Include messages if notifyPatient is true
    if (this.notifyPatient) {
      deleteData.messages = this.messageTemplates;
    }

    this.appointmentService.deleteAppointment(this.editingAppointment.id, deleteData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient appointment deleted successfully');
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error saving patient appointment: ', error);
          this.showToastMessage('There was an error deleting the patient appointment. Please try again.');
        }
      });
  }

  returnToParent() {
    if (this.patientId) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patientId}`] } }]);
    } else {
      let parentPath = this.router.url.includes('schedule') ? 'schedule' : 'list';
      this.router.navigate(['/patients', { outlets: { patient: null } }]).then(() => {
        this.router.navigate(['/patients', parentPath]);
      });
    }
  }

  // Method to determine current status based on appointment data
  determineCurrentStatus(appointment: any): string {
    if (appointment.isCancelled === true) return 'cancelled';
    if (appointment.didNotAttend === true) return 'did_not_attend';
    if (appointment.endTime && appointment.noteSaved === true) return 'completed';
    if (appointment.endTime && appointment.noteSaved === false) return 'completed_no_note';
    if (appointment.startTime && !appointment.endTime) return 'in_progress';
    if (appointment.patientArrivedTime && !appointment.endTime) return 'arrived';
    return 'pending';
  }

  // Method to get available status options based on appointment date and current status
  getAvailableStatusOptions(appointment: any): { value: string, label: string, available: boolean }[] {
    const appointmentDate = moment(appointment.date);
    const now = moment();
    const isFuture = appointmentDate.isAfter(now, 'day');
    const isToday = appointmentDate.isSame(now, 'day');
    const isPast = appointmentDate.isBefore(now, 'day');

    const allOptions = [
      { value: 'pending', label: 'Pending', available: false },
      { value: 'arrived', label: 'Arrived', available: false },
      { value: 'in_progress', label: 'In Progress', available: false },
      { value: 'completed', label: 'Completed', available: false },
      { value: 'completed_no_note', label: 'Completed (No Note)', available: false },
      { value: 'cancelled', label: 'Cancelled', available: false },
      { value: 'did_not_attend', label: 'Did Not Attend', available: false }
    ];

    // Future appointments
    if (isFuture) {
      allOptions.find(opt => opt.value === 'pending')!.available = true;
      allOptions.find(opt => opt.value === 'cancelled')!.available = true;
    }
    // Today's appointments
    else if (isToday) {
      allOptions.find(opt => opt.value === 'pending')!.available = true;
      allOptions.find(opt => opt.value === 'arrived')!.available = true;
      allOptions.find(opt => opt.value === 'in_progress')!.available = true;
      allOptions.find(opt => opt.value === 'completed')!.available = true;
      allOptions.find(opt => opt.value === 'completed_no_note')!.available = true;
      allOptions.find(opt => opt.value === 'cancelled')!.available = true;
      allOptions.find(opt => opt.value === 'did_not_attend')!.available = true;
    }
    // Past appointments
    else if (isPast) {
      allOptions.find(opt => opt.value === 'arrived')!.available = true;
      allOptions.find(opt => opt.value === 'in_progress')!.available = true;
      allOptions.find(opt => opt.value === 'completed')!.available = true;
      allOptions.find(opt => opt.value === 'completed_no_note')!.available = true;
      allOptions.find(opt => opt.value === 'cancelled')!.available = true;
      allOptions.find(opt => opt.value === 'did_not_attend')!.available = true;
    }

    return allOptions;
  }

  // Method to update appointment status
  updateAppointmentStatus(status: string) {
    const appointment = this.editingAppointment;
    if (!appointment) return;

    // Update the appointment object based on status
    switch (status) {
      case 'pending':
        appointment.patientArrivedTime = null;
        appointment.startTime = null;
        appointment.endTime = null;
        appointment.isCancelled = false;
        appointment.didNotAttend = false;
        appointment.noteSaved = null;
        break;
      case 'arrived':
        appointment.patientArrivedTime = moment().toISOString();
        appointment.startTime = null;
        appointment.endTime = null;
        appointment.isCancelled = false;
        appointment.didNotAttend = false;
        appointment.noteSaved = null;
        break;
      case 'in_progress':
        appointment.patientArrivedTime = moment().toISOString();
        appointment.startTime = moment().toISOString();
        appointment.endTime = null;
        appointment.isCancelled = false;
        appointment.didNotAttend = false;
        appointment.noteSaved = null;
        break;
      case 'completed':
        appointment.patientArrivedTime = moment().toISOString();
        appointment.startTime = moment().toISOString();
        appointment.endTime = moment().toISOString();
        appointment.isCancelled = false;
        appointment.didNotAttend = false;
        appointment.noteSaved = true;
        break;
      case 'completed_no_note':
        appointment.patientArrivedTime = moment().toISOString();
        appointment.startTime = moment().toISOString();
        appointment.endTime = moment().toISOString();
        appointment.isCancelled = false;
        appointment.didNotAttend = false;
        appointment.noteSaved = false;
        break;
      case 'cancelled':
        appointment.patientArrivedTime = null;
        appointment.startTime = null;
        appointment.endTime = null;
        appointment.isCancelled = true;
        appointment.didNotAttend = false;
        appointment.noteSaved = null;
        break;
      case 'did_not_attend':
        appointment.patientArrivedTime = null;
        appointment.startTime = null;
        appointment.endTime = null;
        appointment.isCancelled = false;
        appointment.didNotAttend = true;
        appointment.noteSaved = null;
        break;
    }

    // Update the form value
    this.form.patchValue({ status });
  }

}